import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Clock3, RotateCcw, Save, Trash2, X } from "lucide-react";
import { useLocation } from "react-router-dom";

import { getUser, getUserName } from "../../services/storage";
import {
  applyFormDraft,
  getDraftableFields,
  getFormDraftKey,
  getFormDraftLabel,
  isFormDraftEmpty,
  readFormDraft,
  removeFormDraft,
  serializeFormDraft,
  writeFormDraft,
} from "../../utils/formDrafts";

const SAVE_DELAY_MS = 500;
const SUBMIT_SETTLE_MS = 1800;
const ADD_ACTION_PATTERN = /^\+?\s*add\b/i;

const draftTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isManagedForm = (form) => {
  const isNativeForm = form instanceof HTMLFormElement;
  const isDraftScope = form instanceof HTMLElement && form.dataset.draftScope === "true";
  return (
    (isNativeForm || isDraftScope) &&
    form.dataset.draftDisabled !== "true" &&
    !form.closest('[data-draft-disabled="true"]')
  );
};

const getManagedForms = () =>
  Array.from(document.querySelectorAll('form, [data-draft-scope="true"]'))
    .filter(isManagedForm)
    .filter((form) => getDraftableFields(form).length > 0);

const activeFormMatches = (active, form) => {
  if (!active || active.form !== form) return false;
  const fields = getDraftableFields(form);
  return (
    active.fields.length === fields.length &&
    active.fields.every((field, index) => field === fields[index])
  );
};

const rememberActiveForm = (form) => ({
  form,
  fields: getDraftableFields(form),
});

const buttonLabel = (button) =>
  String(
    button?.dataset?.draftAction ||
      button?.getAttribute?.("aria-label") ||
      button?.getAttribute?.("title") ||
      button?.textContent ||
      "",
  )
    .replace(/\s+/g, " ")
    .trim();

const getAddAction = (button, form) => {
  const label = buttonLabel(button);
  if (!label || !ADD_ACTION_PATTERN.test(label)) return null;
  const matchingButtons = Array.from(form.querySelectorAll('button[type="button"]'))
    .filter((candidate) => buttonLabel(candidate) === label);
  return { label, occurrence: Math.max(0, matchingButtons.indexOf(button)) };
};

const nextPaint = () =>
  new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));

const replayAddActions = async (form, actions = []) => {
  for (const action of actions.slice(0, 50)) {
    const matchingButtons = Array.from(
      form.querySelectorAll('button[type="button"]'),
    ).filter((button) => buttonLabel(button) === action.label);
    const button = matchingButtons[action.occurrence || 0];
    if (button && !button.disabled) {
      button.click();
      await nextPaint();
    }
  }
};

export default function FormDraftManager() {
  const location = useLocation();
  const timersRef = useRef(new Map());
  const dismissedRef = useRef(new Set());
  const activeFormsRef = useRef(new Map());
  const savedNoticeRef = useRef(new Set());
  const restoringRef = useRef(false);
  const lastFormRef = useRef(null);
  const lastDescriptionRef = useRef(null);
  const lastDraftRef = useRef(null);
  const addActionsRef = useRef(new Map());
  const [pending, setPending] = useState([]);
  const [status, setStatus] = useState(null);

  const userScope = useMemo(() => {
    const user = getUser();
    return String(user?._id || user?.id || user?.username || getUserName() || "admin");
  }, []);

  const describeForm = useCallback(
    (form) => {
      const forms = getManagedForms();
      const formIndex = Math.max(0, forms.indexOf(form));
      const pathname = window.location.pathname;
      const label = getFormDraftLabel(form);
      const key = getFormDraftKey({ form, pathname, userScope, formIndex });
      return { form, formIndex, pathname, label, key };
    },
    [userScope],
  );

  const scanForDrafts = useCallback(() => {
    const forms = getManagedForms();
    activeFormsRef.current.forEach((activeForm, key) => {
      if (!activeForm?.form?.isConnected) activeFormsRef.current.delete(key);
    });
    const found = forms.flatMap((form) => {
      const description = describeForm(form);
      if (dismissedRef.current.has(description.key)) return [];
      if (activeFormMatches(activeFormsRef.current.get(description.key), form)) return [];
      const draft = readFormDraft(description.key);
      return draft ? [{ ...description, draft }] : [];
    });

    setPending((current) => {
      const signature = found.map(({ key, draft }) => `${key}:${draft.updatedAt}`).join("|");
      const currentSignature = current
        .map(({ key, draft }) => `${key}:${draft.updatedAt}`)
        .join("|");
      return signature === currentSignature ? current : found;
    });
  }, [describeForm]);

  const createDraft = useCallback(
    (form, description) =>
      serializeFormDraft(form, {
        pathname: description.pathname,
        label: description.label,
        addActions:
          addActionsRef.current.get(description.key) ||
          readFormDraft(description.key)?.addActions ||
          [],
      }),
    [],
  );

  const saveNow = useCallback(
    (form, suppliedDescription = null, suppliedDraft = null) => {
      if (!isManagedForm(form) || restoringRef.current) return;
      const description = suppliedDescription || describeForm(form);
      const draft = suppliedDraft || createDraft(form, description);

      if (isFormDraftEmpty(draft)) {
        removeFormDraft(description.key);
        return;
      }

      if (writeFormDraft(description.key, draft)) {
        // Suppress the restore prompt only for this exact live form instance.
        // A modal that closes and mounts again on the same route must be able to
        // discover the saved draft. Using dismissedRef here made those drafts
        // impossible to restore until the user navigated to another route.
        activeFormsRef.current.set(description.key, rememberActiveForm(form));
        dismissedRef.current.delete(description.key);
        setPending((items) => items.filter((item) => item.key !== description.key));
        if (!savedNoticeRef.current.has(description.key)) {
          savedNoticeRef.current.add(description.key);
          setStatus({
            key: description.key,
            label: description.label,
            updatedAt: draft.updatedAt,
            kind: "saved",
          });
        }
      }
    },
    [createDraft, describeForm],
  );

  const scheduleSave = useCallback(
    (form, delay = SAVE_DELAY_MS) => {
      if (!isManagedForm(form) || restoringRef.current) return;
      lastFormRef.current = form;
      const description = describeForm(form);
      const { key } = description;
      const draft = createDraft(form, description);
      lastDescriptionRef.current = description;
      lastDraftRef.current = draft;
      const existing = timersRef.current.get(key);
      if (existing) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        timersRef.current.delete(key);
        // Keep the route and form identity captured while the form was still
        // mounted. Modal forms can disappear before the debounce completes.
        saveNow(form, description, draft);
      }, delay);
      timersRef.current.set(key, timer);
    },
    [createDraft, describeForm, saveNow],
  );

  useEffect(() => {
    dismissedRef.current.clear();
    activeFormsRef.current.clear();
    savedNoticeRef.current.clear();
    const timer = window.setTimeout(scanForDrafts, 150);
    return () => window.clearTimeout(timer);
  }, [location.pathname, scanForDrafts]);

  useEffect(() => {
    const timers = timersRef.current;
    const onFieldChange = (event) => {
      const form = event.target?.closest?.('form, [data-draft-scope="true"]');
      if (form) scheduleSave(form);
    };

    const onClick = (event) => {
      const form = event.target?.closest?.('form, [data-draft-scope="true"]');
      const button = event.target?.closest?.('button[type="button"]');
      if (form && button) {
        // Replaying generic page buttons could repeat an API action such as
        // "Add contact". Structural add-action replay is restricted to real
        // forms, where it is used for adding repeatable rows.
        const action = form instanceof HTMLFormElement
          ? getAddAction(button, form)
          : null;
        if (action) {
          const { key } = describeForm(form);
          const existing =
            addActionsRef.current.get(key) || readFormDraft(key)?.addActions || [];
          addActionsRef.current.set(key, [...existing, action].slice(-50));
        }
        scheduleSave(form, 150);
      }
    };

    const onReset = (event) => {
      if (!isManagedForm(event.target)) return;
      const description = describeForm(event.target);
      removeFormDraft(description.key);
      addActionsRef.current.delete(description.key);
      activeFormsRef.current.delete(description.key);
      dismissedRef.current.add(description.key);
      setPending((items) => items.filter((item) => item.key !== description.key));
      setStatus(null);
    };

    const onSubmit = (event) => {
      const form = event.target;
      if (!isManagedForm(form)) return;
      const description = describeForm(form);
      window.setTimeout(() => {
        if (!form.isConnected) {
          removeFormDraft(description.key);
          addActionsRef.current.delete(description.key);
          activeFormsRef.current.delete(description.key);
          setPending((items) => items.filter((item) => item.key !== description.key));
          setStatus((current) => (current?.key === description.key ? null : current));
          return;
        }
        scheduleSave(form, 0);
      }, SUBMIT_SETTLE_MS);
    };

    const onBeforeUnload = () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      if (lastFormRef.current) {
        saveNow(
          lastFormRef.current,
          lastDescriptionRef.current,
          lastDraftRef.current,
        );
      }
    };

    document.addEventListener("input", onFieldChange, true);
    document.addEventListener("change", onFieldChange, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("reset", onReset, true);
    document.addEventListener("submit", onSubmit, true);
    window.addEventListener("beforeunload", onBeforeUnload);

    // Observe the full document so forms rendered through createPortal (and
    // modals mounted outside .crm-content) are discovered as soon as they open.
    const root = document.body;
    const observer = new MutationObserver((records) => {
      const hasRelevantMutation = records.some(
        (record) => !record.target?.closest?.('[data-form-draft-manager="true"]'),
      );
      if (!hasRelevantMutation) return;
      window.clearTimeout(observer.scanTimer);
      observer.scanTimer = window.setTimeout(scanForDrafts, 120);
      if (
        lastFormRef.current instanceof HTMLFormElement &&
        lastFormRef.current.isConnected
      ) {
        scheduleSave(lastFormRef.current, 250);
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("input", onFieldChange, true);
      document.removeEventListener("change", onFieldChange, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("reset", onReset, true);
      document.removeEventListener("submit", onSubmit, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
      observer.disconnect();
      window.clearTimeout(observer.scanTimer);
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [describeForm, saveNow, scanForDrafts, scheduleSave]);

  useEffect(() => {
    if (!status) return undefined;
    const timer = window.setTimeout(() => setStatus(null), 1600);
    return () => window.clearTimeout(timer);
  }, [status]);

  const currentDraft = pending[0] || null;

  const restoreDraft = async () => {
    if (!currentDraft?.form?.isConnected) {
      setPending((items) => items.slice(1));
      return;
    }

    restoringRef.current = true;
    await replayAddActions(currentDraft.form, currentDraft.draft.addActions);
    const restored = applyFormDraft(currentDraft.form, currentDraft.draft);
    restoringRef.current = false;
    addActionsRef.current.set(
      currentDraft.key,
      Array.isArray(currentDraft.draft.addActions) ? currentDraft.draft.addActions : [],
    );
    activeFormsRef.current.set(
      currentDraft.key,
      rememberActiveForm(currentDraft.form),
    );
    dismissedRef.current.add(currentDraft.key);
    setPending((items) => items.slice(1));
    setStatus({
      key: currentDraft.key,
      label: currentDraft.label,
      updatedAt: currentDraft.draft.updatedAt,
      kind: restored ? "restored" : "saved",
    });
  };

  const discardDraft = () => {
    if (!currentDraft) return;
    removeFormDraft(currentDraft.key);
    addActionsRef.current.delete(currentDraft.key);
    activeFormsRef.current.delete(currentDraft.key);
    dismissedRef.current.add(currentDraft.key);
    setPending((items) => items.slice(1));
  };

  if (!currentDraft && !status) return null;

  return (
    <div
      data-form-draft-manager="true"
      className="pointer-events-none fixed bottom-3 right-3 z-[10000] w-[min(280px,calc(100vw-1.5rem))] sm:bottom-4 sm:right-24"
    >
      {currentDraft ? (
        <section className="pointer-events-auto overflow-hidden rounded-xl border border-amber-200 bg-white shadow-xl">
          <div className="flex items-center gap-2.5 px-3 py-2.5">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-700">
              <Clock3 size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">Draft available</p>
              <p className="truncate text-xs text-slate-500">
                {currentDraft.label} · {draftTime(currentDraft.draft.updatedAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                dismissedRef.current.add(currentDraft.key);
                setPending((items) => items.slice(1));
              }}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close draft notice"
            >
              <X size={17} />
            </button>
          </div>
          <div className="flex gap-2 border-t border-slate-100 bg-slate-50 px-3 py-2">
            <button
              type="button"
              onClick={restoreDraft}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-accentColor px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
            >
              <RotateCcw size={14} /> Restore
            </button>
            <button
              type="button"
              onClick={discardDraft}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              <Trash2 size={14} /> Discard
            </button>
          </div>
        </section>
      ) : (
        <section className="pointer-events-auto flex w-fit max-w-full items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 shadow-lg">
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-emerald-100 text-emerald-700">
            {status.kind === "restored" ? <Check size={15} /> : <Save size={15} />}
          </div>
          <div className="min-w-0">
            <p className="whitespace-nowrap text-xs font-semibold text-slate-800">
              {status.kind === "restored" ? "Draft restored" : "Draft saved"}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
